import React, { useState, useEffect } from 'react';
import DynamicSection from '../components/dynamic/DynamicSection';
import DynamicCard from '../components/dynamic/DynamicCard';
import { faqAPI } from '../services/api';
import { FAQ } from '../types';
import { FaChevronDown, FaChevronUp, FaQuestionCircle } from 'react-icons/fa';

const FAQPage: React.FC = () => {
  const [faqs, setFaqs] = useState<FAQ[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [openIndex, setOpenIndex] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');

  useEffect(() => {
    const loadFAQs = async () => {
      try {
        const response = await faqAPI.getAll();
        if (response && response.data) {
          // Sort by order
          const sorted = Array.isArray(response.data) 
            ? response.data.sort((a, b) => a.order - b.order)
            : [];
          setFaqs(sorted);
        } else {
          setFaqs([]);
        }
      } catch (error: any) {
        console.error('Error loading FAQs:', error);
        setFaqs([]);
      } finally {
        setLoading(false);
      }
    };
    loadFAQs();
  }, []);

  const categories = faqs.length > 0
    ? ['all', ...Array.from(new Set(faqs.map(f => f.category)))]
    : ['all'];
  const filteredFAQs = selectedCategory === 'all' 
    ? faqs 
    : faqs.filter(f => f.category === selectedCategory);

  const toggleFAQ = (faqId: string) => {
    setOpenIndex(openIndex === faqId ? null : faqId);
  };

  if (loading) {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center bg-white dark:bg-gray-900">
        <div className="text-xl text-gray-600 dark:text-gray-400">Loading FAQs...</div>
      </div>
    );
  }

  return (
    <div className="pt-20 bg-white dark:bg-gray-900 min-h-screen">
      <DynamicSection
        title="Frequently Asked Questions"
        subtitle="Find answers to common questions about my services and expertise"
      >
        <div className="mb-8 flex flex-wrap gap-2 justify-center">
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => setSelectedCategory(category)}
              className={`px-4 py-2 rounded-lg font-medium transition-colors capitalize ${
                selectedCategory === category
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
              }`}
            >
              {category}
            </button>
          ))}
        </div>

        {filteredFAQs.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-600 dark:text-gray-400">No FAQs found.</p>
          </div>
        ) : (
          <div className="max-w-3xl mx-auto space-y-4">
            {filteredFAQs.map((faq) => (
              <DynamicCard key={faq.id} className="overflow-hidden">
                <button
                  onClick={() => toggleFAQ(faq.id)}
                  className="w-full p-6 text-left flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                >
                  <div className="flex items-start space-x-4 flex-grow">
                    <FaQuestionCircle className="text-primary-600 dark:text-primary-400 mt-1 flex-shrink-0" />
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      {faq.question}
                    </h3>
                  </div>
                  {openIndex === faq.id ? (
                    <FaChevronUp className="text-gray-500 dark:text-gray-400 flex-shrink-0 ml-4" />
                  ) : (
                    <FaChevronDown className="text-gray-500 dark:text-gray-400 flex-shrink-0 ml-4" />
                  )}
                </button>
                {openIndex === faq.id && (
                <div className="px-6 pb-6 pl-12">
                  <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                    {faq.answer}
                  </p>
                </div>
              )}
              </DynamicCard>
            ))}
          </div>
        )}
      </DynamicSection>
    </div>
  );
};

export default FAQPage;
